/**
 * Component Requirements Declaration Helpers
 * 
 * Provides utilities for components to declare their token dependencies.
 * Each UI component uses these helpers to export its requirements.
 */

import type { 
  TokenPath, 
  DataTokenRequirement, 
  ComponentRequirements,
  DataFieldPath,
  DataTokenCategory,
} from './types';

// ============================================================================
// Component Requirements Builder
// ============================================================================

/**
 * Define complete requirements for a component.
 * Used by UI components to declare their design token and data token dependencies.
 * 
 * @example
 * ```ts
 * export const requirements = defineComponentRequirements('Header', {
 *   designTokens: ['color.primary', 'font.body'],
 *   dataTokens: [
 *     { field: 'property_name', required: true, category: 'branding' },
 *     { field: 'property_primary_logo', required: false, category: 'branding' },
 *   ],
 * });
 * ```
 */
export function defineComponentRequirements(
  componentName: string,
  requirements: ComponentRequirements
): ComponentRequirements & { componentName: string } {
  return {
    componentName,
    ...requirements,
  };
}

// ============================================================================
// Data Token Requirement Builders
// ============================================================================

/**
 * Create a required data token requirement.
 */
export function required(
  field: DataFieldPath,
  category: DataTokenCategory,
  description?: string
): DataTokenRequirement {
  return { field, required: true, category, description };
}

/**
 * Create an optional data token requirement.
 */
export function optional(
  field: DataFieldPath,
  category: DataTokenCategory,
  description?: string
): DataTokenRequirement {
  return { field, required: false, category, description };
}

// ============================================================================
// Shorthand Builders by Category
// ============================================================================

/**
 * Branding field helpers.
 */
export const branding = {
  required: (field: DataFieldPath, description?: string) => 
    required(field, 'branding', description),
  optional: (field: DataFieldPath, description?: string) => 
    optional(field, 'branding', description),
};

/**
 * Contact field helpers.
 */
export const contact = {
  required: (field: DataFieldPath, description?: string) => 
    required(field, 'contact', description),
  optional: (field: DataFieldPath, description?: string) => 
    optional(field, 'contact', description),
};

/**
 * URL field helpers.
 */
export const urls = {
  required: (field: DataFieldPath, description?: string) => 
    required(field, 'urls', description),
  optional: (field: DataFieldPath, description?: string) => 
    optional(field, 'urls', description),
};

/**
 * Content field helpers.
 */
export const content = {
  required: (field: DataFieldPath, description?: string) => 
    required(field, 'content', description),
  optional: (field: DataFieldPath, description?: string) => 
    optional(field, 'content', description),
};

/**
 * Details field helpers.
 */
export const details = {
  required: (field: DataFieldPath, description?: string) => 
    required(field, 'details', description),
  optional: (field: DataFieldPath, description?: string) => 
    optional(field, 'details', description),
};

/**
 * Collection field helpers.
 */
export const collections = {
  required: (field: DataFieldPath, description?: string) => 
    required(field, 'collections', description),
  optional: (field: DataFieldPath, description?: string) => 
    optional(field, 'collections', description),
};

// ============================================================================
// Requirements Registry
// ============================================================================

/**
 * Global registry for component requirements.
 * Components register their requirements here for theme-level aggregation.
 */
const requirementsRegistry = new Map<string, ComponentRequirements>();

/**
 * Register a component's requirements in the global registry.
 */
export function registerComponentRequirements(
  componentName: string,
  requirements: ComponentRequirements
): void {
  requirementsRegistry.set(componentName, requirements);
}

/**
 * Get requirements for a specific component.
 */
export function getComponentRequirements(
  componentName: string
): ComponentRequirements | undefined {
  return requirementsRegistry.get(componentName);
}

/**
 * Get all registered component requirements.
 */
export function getAllComponentRequirements(): Map<string, ComponentRequirements> {
  return new Map(requirementsRegistry);
}

/**
 * Clear the requirements registry (useful for testing).
 */
export function clearRequirementsRegistry(): void {
  requirementsRegistry.clear();
}

// ============================================================================
// Requirements Aggregation
// ============================================================================

/**
 * Aggregate all unique data fields used across multiple components.
 */
export function aggregateDataFields(
  components: ComponentRequirements[]
): DataTokenRequirement[] {
  const fieldMap = new Map<DataFieldPath, DataTokenRequirement>();

  for (const component of components) {
    for (const req of component.dataTokens) {
      const existing = fieldMap.get(req.field);
      if (!existing) {
        fieldMap.set(req.field, req);
      } else if (req.required && !existing.required) {
        // If any component requires the field, mark it as required
        fieldMap.set(req.field, { ...existing, required: true });
      }
    }
  }

  return Array.from(fieldMap.values());
}

/**
 * Aggregate all unique design tokens used across multiple components.
 */
export function aggregateDesignTokens(
  components: ComponentRequirements[]
): TokenPath[] {
  const tokens = new Set<TokenPath>();

  for (const component of components) {
    for (const token of component.designTokens) {
      tokens.add(token);
    }
  }

  return Array.from(tokens);
}

/**
 * Merge multiple component requirements into a single combined requirements object.
 */
export function mergeComponentRequirements(
  components: ComponentRequirements[]
): ComponentRequirements {
  return {
    designTokens: aggregateDesignTokens(components),
    dataTokens: aggregateDataFields(components),
  };
}

